import React from 'react'
import { Bar } from 'react-chartjs-2'
import moment from 'moment'
import {
  Box,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ChartCard from '../../../userPages/dashboard/managerOverview/ChartCard'
import {
  baseChartOptions,
  viz,
} from '../../../userPages/dashboard/managerOverview/overviewTheme'

const truncate = (text, max = 26) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text

const percent = (part, whole) =>
  whole > 0 ? Math.round((part / whole) * 100) : 0

const horizontalOptions = {
  ...baseChartOptions,
  indexAxis: 'y',
  scales: {
    x: { ...baseChartOptions.scales.y },
    y: { ...baseChartOptions.scales.x },
  },
}

const percentAxis = (scales, axis) => ({
  ...scales,
  [axis]: {
    ...scales[axis],
    max: 100,
    ticks: { ...scales[axis].ticks, callback: (value) => `${value}%` },
  },
})

const barStyle = (color = viz.series, horizontal = true) => ({
  backgroundColor: color,
  maxBarThickness: 18,
  borderRadius: horizontal
    ? { topRight: 4, bottomRight: 4 }
    : { topLeft: 4, topRight: 4 },
  borderSkipped: horizontal ? 'start' : 'bottom',
})

const coverageColor = (value) => {
  if (value >= 95) return viz.good
  if (value >= 80) return viz.warning
  return viz.critical
}

// Immunisation coverage - share of screened staff immunised per vaccine,
// lowest coverage first so the gap is at the top
export const ImmunisationCoverageChart = ({ immunisations = [] }) => {
  const rows = immunisations
    .map((item) => ({
      ...item,
      answered: item.immunised + item.notImmunised,
      coverage: percent(item.immunised, item.immunised + item.notImmunised),
    }))
    .filter((item) => item.answered > 0)
    .sort((a, b) => a.coverage - b.coverage)

  if (rows.length === 0) return null

  return (
    <ChartCard
      title="Immunisation coverage"
      subtitle="Screened staff immunised, lowest first"
      height={Math.max(160, rows.length * 34)}
      columns={['Immunisation', 'Immunised', 'Not immunised', 'Coverage']}
      rows={rows.map((item) => [
        item.label,
        item.immunised,
        item.notImmunised,
        `${item.coverage}%`,
      ])}
    >
      <Bar
        aria-label="Bar chart of immunisation coverage per vaccine, lowest coverage first"
        data={{
          labels: rows.map((item) => truncate(item.label)),
          datasets: [
            {
              label: 'Coverage',
              data: rows.map((item) => item.coverage),
              ...barStyle(rows.map((item) => coverageColor(item.coverage))),
            },
          ],
        }}
        options={{
          ...horizontalOptions,
          scales: percentAxis(horizontalOptions.scales, 'x'),
          plugins: {
            ...baseChartOptions.plugins,
            tooltip: {
              ...baseChartOptions.plugins.tooltip,
              callbacks: {
                title: (items) => rows[items[0].dataIndex]?.label,
                label: (ctx) => {
                  const item = rows[ctx.dataIndex]
                  return ` ${ctx.raw}% immunised (${item.immunised}/${item.answered} staff)`
                },
              },
            },
          },
        }}
      />
    </ChartCard>
  )
}

// Which kinds of flag are raised across the workforce - one count per
// person per category so a single person with five conditions counts once
export const FlagBreakdownChart = ({ flagCategories = [], screened = 0 }) => {
  if (flagCategories.length === 0) return null
  const rows = [...flagCategories].sort((a, b) => b.count - a.count)

  return (
    <ChartCard
      title="Flags by category"
      subtitle="Staff whose latest screening raised each kind of flag"
      height={Math.max(160, rows.length * 34)}
      columns={['Category', 'Staff', 'Share of screened']}
      rows={rows.map((item) => [
        item.label,
        item.count,
        `${percent(item.count, screened)}%`,
      ])}
    >
      <Bar
        aria-label="Bar chart of staff count per occupational health flag category"
        data={{
          labels: rows.map((item) => item.label),
          datasets: [
            {
              label: 'Staff',
              data: rows.map((item) => item.count),
              ...barStyle(viz.warning),
            },
          ],
        }}
        options={{
          ...horizontalOptions,
          plugins: {
            ...baseChartOptions.plugins,
            tooltip: {
              ...baseChartOptions.plugins.tooltip,
              callbacks: {
                label: (ctx) =>
                  ` ${ctx.raw} staff member${ctx.raw === 1 ? '' : 's'} (${percent(
                    ctx.raw,
                    screened,
                  )}% of screened)`,
              },
            },
          },
        }}
      />
    </ChartCard>
  )
}

const BMI_BANDS = [
  { key: 'underweight', label: 'Underweight', range: 'under 18.5' },
  { key: 'healthy', label: 'Healthy', range: '18.5 – 24.9' },
  { key: 'overweight', label: 'Overweight', range: '25 – 29.9' },
  { key: 'obese', label: 'Obese', range: '30 – 39.9' },
  { key: 'severelyObese', label: 'Severely obese', range: '40 and over' },
]

// BMI distribution from the calculated BMI on each latest screening
export const BmiDistributionChart = ({ bmi }) => {
  if (!bmi) return null
  const total = BMI_BANDS.reduce((sum, band) => sum + (bmi[band.key] ?? 0), 0)
  if (total === 0) return null

  return (
    <ChartCard
      title="BMI distribution"
      subtitle="Calculated from each person's latest screening"
      columns={['Band', 'BMI', 'Staff']}
      rows={BMI_BANDS.map((band) => [
        band.label,
        band.range,
        bmi[band.key] ?? 0,
      ])}
    >
      <Bar
        aria-label="Bar chart of staff count per BMI band"
        data={{
          labels: BMI_BANDS.map((band) => band.label),
          datasets: [
            {
              label: 'Staff',
              data: BMI_BANDS.map((band) => bmi[band.key] ?? 0),
              ...barStyle(viz.series, false),
              maxBarThickness: 36,
            },
          ],
        }}
        options={{
          ...baseChartOptions,
          plugins: {
            ...baseChartOptions.plugins,
            tooltip: {
              ...baseChartOptions.plugins.tooltip,
              callbacks: {
                title: (items) => {
                  const band = BMI_BANDS[items[0].dataIndex]
                  return `${band.label} (BMI ${band.range})`
                },
                label: (ctx) =>
                  ` ${ctx.raw} staff member${ctx.raw === 1 ? '' : 's'}`,
              },
            },
          },
        }}
      />
    </ChartCard>
  )
}

// Screenings submitted per month - confirmations at appraisal count too
export const SubmissionsTrendChart = ({ submissionsByMonth = [] }) => (
  <ChartCard
    title="Screenings completed"
    subtitle="Submissions and reconfirmations per month, last 12 months"
    columns={['Month', 'Screenings']}
    rows={submissionsByMonth.map((m) => [
      moment(m.month, 'YYYY-MM').format('MMMM YYYY'),
      m.count,
    ])}
  >
    <Bar
      aria-label="Bar chart of occupational health screenings submitted per month over the last twelve months"
      data={{
        labels: submissionsByMonth.map((m) =>
          moment(m.month, 'YYYY-MM').format('MMM'),
        ),
        datasets: [
          {
            label: 'Screenings',
            data: submissionsByMonth.map((m) => m.count),
            ...barStyle(viz.series, false),
            maxBarThickness: 20,
          },
        ],
      }}
      options={{
        ...baseChartOptions,
        plugins: {
          ...baseChartOptions.plugins,
          tooltip: {
            ...baseChartOptions.plugins.tooltip,
            callbacks: {
              title: (items) =>
                moment(
                  submissionsByMonth[items[0].dataIndex]?.month,
                  'YYYY-MM',
                ).format('MMMM YYYY'),
              label: (ctx) =>
                ` ${ctx.raw} screening${ctx.raw === 1 ? '' : 's'}`,
            },
          },
        },
      }}
    />
  </ChartCard>
)

// Most commonly declared conditions and selected COVID-19 risks
export const DeclarationsChart = ({ conditions = [], covidRisks = [] }) => {
  const rows = [
    ...conditions.map((item) => ({ ...item, kind: 'Condition' })),
    ...covidRisks.map((item) => ({ ...item, kind: 'COVID-19 risk' })),
  ]
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)

  if (rows.length === 0) return null

  return (
    <ChartCard
      title="Most declared"
      subtitle="Conditions and COVID-19 risks across latest screenings"
      height={Math.max(160, rows.length * 34)}
      columns={['Declaration', 'Type', 'Staff']}
      rows={rows.map((item) => [item.label, item.kind, item.count])}
    >
      <Bar
        aria-label="Bar chart of the most commonly declared conditions and COVID-19 risks"
        data={{
          labels: rows.map((item) => truncate(item.label)),
          datasets: [
            {
              label: 'Staff',
              data: rows.map((item) => item.count),
              ...barStyle(
                rows.map((item) =>
                  item.kind === 'Condition' ? viz.series : viz.deEmphasis,
                ),
              ),
            },
          ],
        }}
        options={{
          ...horizontalOptions,
          plugins: {
            ...baseChartOptions.plugins,
            tooltip: {
              ...baseChartOptions.plugins.tooltip,
              callbacks: {
                title: (items) => rows[items[0].dataIndex]?.label,
                label: (ctx) => {
                  const item = rows[ctx.dataIndex]
                  return ` ${item.kind}: ${ctx.raw} staff member${
                    ctx.raw === 1 ? '' : 's'
                  }`
                },
              },
            },
          },
        }}
      />
    </ChartCard>
  )
}

// Fitness requirements - every check has a pass share; one card, one bar
// per check, with the people who can't complete it called out in the table
export const FitnessCoverageCard = ({ fitness = [] }) => {
  const rows = fitness
    .map((item) => ({
      ...item,
      answered: item.able + item.unable,
      share: percent(item.able, item.able + item.unable),
    }))
    .filter((item) => item.answered > 0)
    .sort((a, b) => a.share - b.share)

  if (rows.length === 0) return null

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack>
        <Typography variant="title">Physical fitness checks</Typography>
        <Typography variant="smallGrey">
          Share of screened staff able to complete each requirement
        </Typography>
      </Stack>
      <Stack gap={1.5} mt={2}>
        {rows.map((item) => (
          <Box key={item.label}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: coverageColor(item.share),
                  whiteSpace: 'nowrap',
                }}
              >
                {item.share}%
                {item.unable > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    {item.unable} unable
                  </Typography>
                )}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={item.share}
              aria-label={`${item.label}: ${item.share}% able`}
              sx={{
                mt: 0.5,
                height: 8,
                borderRadius: 4,
                backgroundColor: viz.grid,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: coverageColor(item.share),
                },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Paper>
  )
}

// Coverage by clinical role - which teams are behind
export const RoleCoverageTable = ({ byRole = [] }) => {
  if (byRole.length === 0) return null

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack mb={1}>
        <Typography variant="title">Coverage by role</Typography>
        <Typography variant="smallGrey">
          Roles with the most outstanding screenings first
        </Typography>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.78rem' }}>Role</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                Staff
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                Outstanding
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                Flagged
              </TableCell>
              <TableCell sx={{ fontSize: '0.78rem', minWidth: 140 }}>
                Screened
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {byRole.map((row) => {
              const share = percent(row.complete, row.total)
              return (
                <TableRow key={row.role}>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.role}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '0.8rem',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {row.total}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '0.8rem',
                      fontVariantNumeric: 'tabular-nums',
                      color:
                        row.outstanding > 0 ? viz.critical : 'text.primary',
                      fontWeight: row.outstanding > 0 ? 600 : 400,
                    }}
                  >
                    {row.outstanding}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '0.8rem',
                      fontVariantNumeric: 'tabular-nums',
                      color: row.flagged > 0 ? viz.warning : 'text.primary',
                    }}
                  >
                    {row.flagged}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <LinearProgress
                        variant="determinate"
                        value={share}
                        aria-label={`${row.role}: ${share}% screened`}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: viz.grid,
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            backgroundColor: coverageColor(share),
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          minWidth: 34,
                        }}
                      >
                        {share}%
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  )
}
